<section class="relative flex h-full w-full flex-col overflow-hidden bg-white text-slate-900" role="dialog">
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-5 py-3">
    <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg" [ngClass]="typeStyles[type].class">
      <app-icon>{{ typeStyles[type].icon }}</app-icon>
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-center gap-2">
        <h2 class="m-0 truncate text-base font-bold leading-6 text-slate-900">
          {{ 'stock.doc_type.' + type | translate }}
          @if (document) {
            <span class="font-mono text-sm font-semibold text-slate-400">{{ getDocumentNumber(document.id) }}</span>
          }
        </h2>
        <span
          class="rounded-full px-2 py-0.5 text-xs font-semibold"
          [ngClass]="statusClasses[document?.status ?? 'DRAFT']"
        >
          {{ 'stock.doc_status.' + (document?.status ?? 'DRAFT') | translate }}
        </span>
        @if (isOpenTransfer) {
          <span class="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
            {{ 'stock.document.in_transit_badge' | translate }}
          </span>
        }
        @if (document?.source === 'CARD') {
          <span class="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">
            {{ 'stock.document.from_card' | translate }}
          </span>
        }
      </div>
      @if (document) {
        <div class="text-xs text-slate-400">
          {{ 'created' | translate }} {{ document.created_at | date: 'dd.MM.yyyy HH:mm' }}
        </div>
      }
    </div>
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
      [attr.aria-label]="'close' | translate"
      (click)="requestClose()"
    >
      <app-icon>close</app-icon>
    </button>
  </header>

  @if (loading) {
    <div class="flex flex-1 flex-col gap-4 bg-slate-50 p-5">
      <div class="h-40 animate-pulse rounded-lg bg-white"></div>
      <div class="h-72 animate-pulse rounded-lg bg-white"></div>
    </div>
  } @else if (loadFailed) {
    <div class="flex flex-1 flex-col items-center justify-center gap-3 bg-slate-50 p-5 text-center">
      <app-icon class="!text-[32px] text-slate-300">error_outline</app-icon>
      <p class="m-0 text-sm text-slate-600">{{ 'stock.load_failed' | translate }}</p>
      <app-button btnType="stroked" (onChangeClick)="load()">{{ 'stock.retry' | translate }}</app-button>
    </div>
  } @else {
    <div class="crm-scroll min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-4">
      <div class="flex flex-col gap-4">
        <section class="rounded-lg border !border-slate-200 bg-white p-4">
          @if (isDraft) {
            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <app-select
                [formControl]="form.controls.warehouse_id"
                [options]="warehouses"
                optionLabel="name"
                optionValue="id"
                [filter]="true"
                [required]="true"
                [label]="(type === 'TRANSFER' ? 'stock.document.sender' : 'warehouse') | translate"
              />
              @if (type === 'TRANSFER') {
                <app-select
                  [formControl]="form.controls.target_warehouse_id"
                  [options]="warehouses"
                  optionLabel="name"
                  optionValue="id"
                  [filter]="true"
                  [required]="true"
                  [label]="'stock.document.receiver' | translate"
                />
              }
              <app-select
                [formControl]="form.controls.currency"
                [options]="currencyOptions"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                [label]="'stock.currency' | translate"
                [placeholder]="'stock.document.currency_auto' | translate"
              />
              <app-select
                [formControl]="form.controls.manager_id"
                [options]="managers"
                optionLabel="name"
                optionValue="id"
                [filter]="true"
                [showClear]="true"
                [isAvatar]="true"
                [label]="'manager' | translate"
              />
              <app-textarea
                class="md:col-span-2"
                [formControl]="form.controls.reason"
                [label]="(type === 'WRITE_OFF' ? 'stock.document.write_off_reason' : 'comment') | translate"
                [maxLength]="1000"
              />
            </div>
          } @else if (document) {
            <dl class="m-0 grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
              <div>
                <dt class="text-xs font-medium text-slate-400">
                  {{ (type === 'TRANSFER' ? 'stock.document.sender' : 'warehouse') | translate }}
                </dt>
                <dd class="m-0 font-medium text-slate-800">{{ document.warehouse?.name ?? '—' }}</dd>
              </div>
              @if (type === 'TRANSFER') {
                <div>
                  <dt class="text-xs font-medium text-slate-400">{{ 'stock.document.receiver' | translate }}</dt>
                  <dd class="m-0 font-medium text-slate-800">{{ document.target_warehouse?.name ?? '—' }}</dd>
                </div>
              }
              <div>
                <dt class="text-xs font-medium text-slate-400">{{ 'manager' | translate }}</dt>
                <dd class="m-0 flex items-center gap-2 font-medium text-slate-800">
                  @if (document.manager; as manager) {
                    <app-avatar
                      [url]="getManager(manager.id)?.avatarUrl"
                      [title]="formatFullName(manager.first_name, manager.last_name)"
                      size="22px"
                      [isPhotoGallery]="false"
                    />
                    {{ formatFullName(manager.first_name, manager.last_name) }}
                  } @else {
                    —
                  }
                </dd>
              </div>
              <div>
                <dt class="text-xs font-medium text-slate-400">{{ 'stock.document.posted_at' | translate }}</dt>
                <dd class="m-0 font-medium text-slate-800">
                  {{ (document.posted_at | date: 'dd.MM.yyyy HH:mm') ?? '—' }}
                </dd>
              </div>
              @if (document.cancelled_at) {
                <div>
                  <dt class="text-xs font-medium text-slate-400">{{ 'stock.document.cancelled_at' | translate }}</dt>
                  <dd class="m-0 font-medium text-rose-600">{{ document.cancelled_at | date: 'dd.MM.yyyy HH:mm' }}</dd>
                </div>
              }
              @if (type === 'TRANSFER' && document.closed_at) {
                <div>
                  <dt class="text-xs font-medium text-slate-400">{{ 'stock.document.closed_at' | translate }}</dt>
                  <dd class="m-0 font-medium text-slate-800">
                    {{ document.closed_at | date: 'dd.MM.yyyy HH:mm' }}
                    <span class="text-xs text-slate-500">
                      · {{ 'stock.close_resolution.' + (document.close_resolution ?? 'ACCEPTED') | translate }}
                    </span>
                  </dd>
                </div>
              }
              @if (document.card; as card) {
                <div>
                  <dt class="text-xs font-medium text-slate-400">{{ 'stock.card' | translate }}</dt>
                  <dd class="m-0">
                    <button
                      type="button"
                      class="truncate text-left font-medium text-orange-600 hover:underline"
                      (click)="openCard(card.id)"
                    >
                      {{ card.name }}
                    </button>
                  </dd>
                </div>
              }
              @if (document.source_document_id; as sourceId) {
                <div>
                  <dt class="text-xs font-medium text-slate-400">{{ 'stock.document.source_document' | translate }}</dt>
                  <dd class="m-0">
                    <button
                      type="button"
                      class="font-mono text-sm font-medium text-orange-600 hover:underline"
                      (click)="openDocument(sourceId)"
                    >
                      {{ getDocumentNumber(sourceId) }}
                    </button>
                  </dd>
                </div>
              }
              @if (document.reason) {
                <div class="md:col-span-3">
                  <dt class="text-xs font-medium text-slate-400">
                    {{ (type === 'WRITE_OFF' ? 'stock.document.write_off_reason' : 'comment') | translate }}
                  </dt>
                  <dd class="m-0 whitespace-pre-wrap text-slate-700">{{ document.reason }}</dd>
                </div>
              }
              @if (document.close_reason) {
                <div class="md:col-span-3">
                  <dt class="text-xs font-medium text-slate-400">{{ 'stock.document.close_reason' | translate }}</dt>
                  <dd class="m-0 whitespace-pre-wrap text-slate-700">{{ document.close_reason }}</dd>
                </div>
              }
            </dl>
          }
        </section>

        <section class="rounded-lg border !border-slate-200 bg-white">
          <div class="flex items-center gap-2 border-b !border-slate-100 px-4 py-3">
            <h3 class="m-0 text-sm font-semibold text-slate-900">{{ 'stock.document.lines' | translate }}</h3>
            <span class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{{
              rows.length
            }}</span>
            @if (isDraft) {
              <span class="ml-auto text-xs text-slate-400">{{ 'stock.document.lines_hint.' + type | translate }}</span>
            }
          </div>

          <app-table-v2
            layout="bare"
            size="sm"
            minWidth="min-w-[720px]"
            [columns]="lineColumns"
            [rows]="rows"
            [actions]="isDraft ? lineActions : []"
            actionsClass="w-12"
            [rowClass]="shortageRowClass"
            emptyText="stock.document.no_lines"
          >
            <ng-template appTableV2Cell="product" [appTableV2CellOf]="rows" let-row>
              <div class="flex min-w-0 items-center gap-3">
                <app-warehouse-thumb [fileId]="row.variant?.photo_id" size="sm" />
                <div class="min-w-0">
                  <div class="truncate font-medium text-slate-900">
                    {{ getVariantTitle(row.variant) || ('stock.deleted_variant' | translate) }}
                  </div>
                  <div class="flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
                    <span>{{ getVariantCodes(row.variant) }}</span>
                    @if (row.variant?.is_archived) {
                      <span class="text-amber-600">{{ 'stock.archived' | translate }}</span>
                    }
                    @if (shortages.get(row.variant_id); as shortage) {
                      <span class="font-medium text-rose-600">
                        {{
                          'stock.document.shortage'
                            | translate
                              : { available: formatQuantity(shortage.available), warehouse: shortage.warehouse_name }
                        }}
                      </span>
                    }
                  </div>
                </div>
              </div>
            </ng-template>
            <ng-template appTableV2Cell="quantity" [appTableV2CellOf]="rows" let-row>
              <ng-container [ngTemplateOutlet]="numberInput" [ngTemplateOutletContext]="{ row, field: 'quantity' }" />
            </ng-template>
            <ng-template appTableV2Cell="actual_quantity" [appTableV2CellOf]="rows" let-row>
              <ng-container
                [ngTemplateOutlet]="numberInput"
                [ngTemplateOutletContext]="{ row, field: 'actual_quantity' }"
              />
            </ng-template>
            <ng-template appTableV2Cell="price" [appTableV2CellOf]="rows" let-row>
              <ng-container [ngTemplateOutlet]="numberInput" [ngTemplateOutletContext]="{ row, field: 'price' }" />
            </ng-template>
            <ng-template appTableV2Cell="accept" [appTableV2CellOf]="rows" let-row>
              @if (row.line?.in_transit_quantity) {
                <ng-container [ngTemplateOutlet]="numberInput" [ngTemplateOutletContext]="{ row, field: 'accept' }" />
              } @else {
                <span class="text-slate-300">—</span>
              }
            </ng-template>
          </app-table-v2>

          @if (isDraft) {
            <div class="border-t !border-slate-100 px-4 py-3">
              <app-warehouse-variant-picker [selected]="rows" (picked)="addVariant($event)" />
            </div>
          }

          @if (type === 'RECEIPT' || !isDraft) {
            <div class="flex items-center justify-end gap-3 border-t !border-slate-100 px-4 py-3 text-sm">
              <span class="text-slate-500">{{ 'stock.document.total' | translate }}</span>
              <span class="text-base font-bold text-slate-900">{{ formatMoneyAmount(total, currency) }}</span>
            </div>
          }
        </section>

        @if (isOpenTransfer && closePanelOpen) {
          <section class="flex flex-col gap-3 rounded-lg border !border-amber-200 bg-amber-50/60 p-4">
            <h3 class="m-0 text-sm font-semibold text-slate-900">{{ 'stock.document.close_title' | translate }}</h3>
            <p class="m-0 text-xs text-slate-600">
              {{ 'stock.document.close_hint' | translate: { count: formatQuantity(totalInTransit) } }}
            </p>
            <div class="flex flex-col gap-2">
              @for (resolution of ['RETURN', 'LOSS']; track resolution) {
                <app-radio
                  name="close-resolution"
                  [value]="resolution"
                  [label]="'stock.document.close_' + resolution | translate"
                  [(ngModel)]="closeResolution"
                />
              }
            </div>
            <textarea
              rows="2"
              maxlength="1000"
              class="w-full rounded-lg border !border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:!border-orange-400"
              [placeholder]="
                (closeResolution === 'LOSS' ? 'stock.document.loss_reason' : 'stock.document.close_comment') | translate
              "
              [(ngModel)]="closeReason"
            ></textarea>
            <div class="flex justify-end gap-2">
              <app-button btnType="cancel" [disabled]="busy" (onChangeClick)="closePanelOpen = false">{{
                'cancel' | translate
              }}</app-button>
              <app-button [disabled]="busy" (onChangeClick)="closeTransfer()">{{
                'stock.document.close_confirm' | translate
              }}</app-button>
            </div>
          </section>
        }

        @if (formError) {
          <div class="rounded-lg border !border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {{ formError | translate }}
          </div>
        }
      </div>
    </div>

    <footer class="flex shrink-0 flex-wrap items-center gap-2 border-t !border-slate-100 px-5 py-3">
      @if (isDraft) {
        <button
          type="button"
          class="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-rose-600 transition hover:bg-rose-50 disabled:opacity-50"
          [disabled]="busy"
          (click)="deleteDraft()"
        >
          <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">delete</app-icon>
          {{ (document ? 'stock.document.delete_draft' : 'cancel') | translate }}
        </button>
        <div class="ml-auto flex flex-wrap gap-2">
          <app-button
            btnType="stroked"
            [customClass]="'!h-10 !rounded-lg'"
            [disabled]="busy"
            (onChangeClick)="saveDraft()"
          >
            {{ 'stock.document.save_draft' | translate }}
          </app-button>
          <app-button [customClass]="'!h-10 !rounded-lg'" [disabled]="busy || !rows.length" (onChangeClick)="post()">
            <span class="flex items-center gap-1.5">
              <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">task_alt</app-icon>
              {{ 'stock.document.post' | translate }}
            </span>
          </app-button>
        </div>
      } @else if (document) {
        <button
          type="button"
          class="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          (click)="openMovements(document.id)"
        >
          <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">receipt_long</app-icon>
          {{ 'warehouse_movements' | translate }}
        </button>
        <div class="ml-auto flex flex-wrap gap-2">
          @if (document.status === 'POSTED') {
            <app-button
              btnType="cancel"
              [customClass]="'!h-10 !rounded-lg'"
              [disabled]="busy"
              (onChangeClick)="cancelDocument()"
            >
              {{ 'stock.document.cancel_document' | translate }}
            </app-button>
          }
          @if (isOpenTransfer) {
            <app-button
              btnType="stroked"
              [customClass]="'!h-10 !rounded-lg'"
              [disabled]="busy"
              (onChangeClick)="closePanelOpen = !closePanelOpen"
            >
              {{ 'stock.document.close_transfer' | translate }}
            </app-button>
            <app-button [customClass]="'!h-10 !rounded-lg'" [disabled]="busy" (onChangeClick)="acceptTransfer()">
              <span class="flex items-center gap-1.5">
                <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">inventory</app-icon>
                {{ 'accept' | translate }}
              </span>
            </app-button>
          }
        </div>
      }
    </footer>
  }
</section>

<ng-template #numberInput let-row="row" let-field="field">
  <input
    type="text"
    inputmode="decimal"
    class="h-8 w-28 rounded-md border bg-white px-2 text-right text-sm text-slate-800 outline-none focus:!border-orange-400"
    [ngClass]="row.invalid.has(field) ? '!border-red-400' : '!border-slate-200'"
    [ngModel]="row[field]"
    (ngModelChange)="onRowInput(row, field, $event)"
  />
</ng-template>
